import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ActionIcon, Box, Paper } from '@mantine/core';
import { useClickOutside, useDebouncedValue } from '@mantine/hooks';
import {
	$getSelection,
	$isRangeSelection,
	$setSelection,
	COMMAND_PRIORITY_LOW,
	FORMAT_TEXT_COMMAND,
	SELECTION_CHANGE_COMMAND
} from 'lexical';
import {
	type MouseEvent,
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react';
import { createPortal } from 'react-dom';

const BUTTON_SIZE = 32;
const TOOLBAR_GAP = 4;
const TOOLBAR_PADDING = 4;
const DEFAULT_TOOLBAR_HEIGHT = BUTTON_SIZE + TOOLBAR_PADDING * 2;
const DEFAULT_TOOLBAR_WIDTH =
	BUTTON_SIZE * 2 + TOOLBAR_GAP + TOOLBAR_PADDING * 2;
const MIN_SELECTION_CLEARANCE = 12;
const MAX_SELECTION_CLEARANCE = 24;
const VIEWPORT_PADDING = 8;

const FloatingFormatToolbar = () => {
	const [editor] = useLexicalComposerContext();

	const [selectionRect, setSelectionRect] = useState<DOMRect | null>(null);
	const [visible, setVisible] = useState(false);
	const [activeFormats, setActiveFormats] = useState({
		bold: false,
		italic: false
	});

	const toolbarRef = useRef<HTMLDivElement | null>(null);
	const lastSelectionRef = useRef<ReturnType<typeof $getSelection> | null>(
		null
	);

	const hideToolbar = useCallback(() => {
		setSelectionRect(null);
		setVisible(false);
		setActiveFormats({ bold: false, italic: false });
		lastSelectionRef.current = null;
	}, []);

	const updateToolbarState = useCallback(() => {
		editor.getEditorState().read(() => {
			if (!editor.isEditable()) {
				hideToolbar();
				return;
			}

			const selection = $getSelection();

			if (!$isRangeSelection(selection) || selection.isCollapsed()) {
				hideToolbar();
				return;
			}

			const domSelection = window.getSelection();

			if (
				!domSelection ||
				domSelection.type !== 'Range' ||
				domSelection.rangeCount === 0
			) {
				hideToolbar();
				return;
			}

			const range = domSelection.getRangeAt(0);
			const rects = range.getClientRects();

			if (rects.length === 0) {
				hideToolbar();
				return;
			}

			const rect = rects[0];

			lastSelectionRef.current = selection.clone();
			setActiveFormats({
				bold: selection.hasFormat('bold'),
				italic: selection.hasFormat('italic')
			});
			setSelectionRect(new DOMRect(rect.x, rect.y, rect.width, rect.height));
		});
	}, [editor, hideToolbar]);

	useClickOutside(
		hideToolbar,
		undefined,
		toolbarRef.current ? [toolbarRef.current] : []
	);

	useEffect(() => {
		const unregisterUpdate = editor.registerUpdateListener(() => {
			updateToolbarState();
		});

		const unregisterSelectionChange = editor.registerCommand(
			SELECTION_CHANGE_COMMAND,
			() => {
				updateToolbarState();
				return false;
			},
			COMMAND_PRIORITY_LOW
		);

		return () => {
			unregisterUpdate();
			unregisterSelectionChange();
		};
	}, [editor, updateToolbarState]);

	useEffect(() => {
		return editor.registerRootListener((rootElement, prevRootElement) => {
			prevRootElement?.removeEventListener('focusout', hideToolbar);
			rootElement?.addEventListener('focusout', hideToolbar);
		});
	}, [editor, hideToolbar]);

	const [debouncedRect] = useDebouncedValue(selectionRect, 250);

	useEffect(() => {
		setVisible(Boolean(debouncedRect));
	}, [debouncedRect]);

	const position = debouncedRect
		? (() => {
				const toolbarHeight =
					toolbarRef.current?.offsetHeight ?? DEFAULT_TOOLBAR_HEIGHT;
				const toolbarWidth =
					toolbarRef.current?.offsetWidth ?? DEFAULT_TOOLBAR_WIDTH;
				const selectionClearance = Math.min(
					Math.max(debouncedRect.height, MIN_SELECTION_CLEARANCE),
					MAX_SELECTION_CLEARANCE
				);
				const centeredLeft = debouncedRect.left + debouncedRect.width / 2;
				const minLeft = VIEWPORT_PADDING + toolbarWidth / 2;
				const maxLeft = window.innerWidth - VIEWPORT_PADDING - toolbarWidth / 2;
				const left = Math.min(Math.max(centeredLeft, minLeft), maxLeft);
				const aboveTop = debouncedRect.top - toolbarHeight - selectionClearance;
				const belowTop = debouncedRect.bottom + selectionClearance;
				const maxTop = window.innerHeight - toolbarHeight - VIEWPORT_PADDING;
				const top =
					aboveTop >= VIEWPORT_PADDING ? aboveTop : Math.min(belowTop, maxTop);

				return { top, left };
			})()
		: null;

	if (!visible || !debouncedRect || !position) {
		return null;
	}

	const handleFormatMouseDown =
		(format: 'bold' | 'italic') => (event: MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();

			editor.update(() => {
				const lastSelection = lastSelectionRef.current;
				if (lastSelection && $isRangeSelection(lastSelection)) {
					$setSelection(lastSelection.clone());
				}
				editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
			});
		};

	return createPortal(
		<Paper
			display="flex"
			p={TOOLBAR_PADDING}
			pos="fixed"
			radius="xl"
			ref={toolbarRef}
			shadow="xl"
			style={{
				top: position.top,
				left: position.left,
				transform: 'translateX(-50%)'
			}}
			styles={{
				root: {
					zIndex: 9999,
					gap: TOOLBAR_GAP,
					alignItems: 'center',
					backgroundColor: 'rgba(255, 255, 255, 0.92)',
					backdropFilter: 'blur(12px)',
					borderColor: 'rgba(15, 23, 42, 0.08)',
					boxShadow: '0 12px 32px rgba(15, 23, 42, 0.14)',
					transition: 'opacity 150ms ease, transform 150ms ease'
				}
			}}
			withBorder
		>
			<ActionIcon
				color={activeFormats.bold ? 'dark' : 'gray'}
				onMouseDown={handleFormatMouseDown('bold')}
				radius="md"
				size={BUTTON_SIZE}
				styles={{
					root: {
						color: activeFormats.bold
							? 'var(--mantine-color-white)'
							: 'var(--mantine-color-gray-7)',
						backgroundColor: activeFormats.bold
							? 'var(--mantine-color-dark-6)'
							: 'transparent',
						border: activeFormats.bold
							? '1px solid var(--mantine-color-dark-6)'
							: '1px solid transparent',
						transition: 'transform 120ms ease, background-color 120ms ease'
					}
				}}
				type="button"
				variant={activeFormats.bold ? 'filled' : 'subtle'}
			>
				<Box
					component="span"
					fw={700}
				>
					B
				</Box>
			</ActionIcon>

			<ActionIcon
				color={activeFormats.italic ? 'dark' : 'gray'}
				onMouseDown={handleFormatMouseDown('italic')}
				radius="md"
				size={BUTTON_SIZE}
				styles={{
					root: {
						color: activeFormats.italic
							? 'var(--mantine-color-white)'
							: 'var(--mantine-color-gray-7)',
						backgroundColor: activeFormats.italic
							? 'var(--mantine-color-dark-6)'
							: 'transparent',
						border: activeFormats.italic
							? '1px solid var(--mantine-color-dark-6)'
							: '1px solid transparent',
						transition: 'transform 120ms ease, background-color 120ms ease'
					}
				}}
				type="button"
				variant={activeFormats.italic ? 'filled' : 'subtle'}
			>
				<Box
					component="span"
					fs="italic"
				>
					I
				</Box>
			</ActionIcon>
		</Paper>,
		document.body
	);
};

export default FloatingFormatToolbar;
